/**
 * Slash commands in the chat composer (owner, 2026-10-31: "we need to add slash commands to gyro, such as being able to
 * set goals"). Typing `/` as the first character opens a menu above the message box; the person picks a command with
 * the keys or finishes typing it and presses Enter. A slash command is handled by the widget (or, for /goal, by one
 * small gyro-svc route) and never reaches the model as chat text.
 *
 * This module is pure and DOM-free (test/slashCommands.test.ts): what the composer holds is read here, the commands are
 * ranked here with the palette's own ranker (palette.ts), and the /goal words and notes are decided here. The
 * commands other than /goal and /help are spellings of the Ctrl/Cmd+K palette's commands (PALETTE_SLASH_ALIASES):
 * the same handlers, so nothing new happens that the palette does not do.
 */
import { rankPalette } from './palette.js';
/**
 * The slash spellings of the palette's commands, by palette entry id. The description is the palette's own label (with
 * its hint) unless given here, so a label that changes with state ("Expand widget" / "Collapse widget") reads the same
 * in both places. A palette command absent from this table has no slash spelling.
 */
export const PALETTE_SLASH_ALIASES = {
    'cmd-new-chat': { name: 'new', aliases: ['clear'], description: 'Start a new chat' },
    'cmd-fork-chat': { name: 'fork', description: 'Fork this chat into a new one' },
    'cmd-search-chats': { name: 'search', aliases: ['history', 'chats'], description: 'Search your conversations' },
    'cmd-memory': { name: 'memory', description: 'Open what Gyro remembers about you' },
    'cmd-capabilities': { name: 'capabilities', aliases: ['tools'], description: 'See what Gyro can do here' },
    'cmd-toggle-voice': { name: 'voice' },
    'cmd-talk': { name: 'talk', aliases: ['dictate'] },
    'cmd-report-bug': { name: 'bug', aliases: ['report'], description: 'Report a bug to The Lab' },
    'cmd-create-ppp': { name: 'ppp' },
    'cmd-settings': { name: 'settings' },
    'cmd-guide': { name: 'guide', aliases: ['manual'] },
    'cmd-notification-center': { name: 'notifications', aliases: ['inbox'] },
    'cmd-expand': { name: 'expand', aliases: ['resize'] },
    'cmd-window-mode': { name: 'window' },
    'cmd-notifications': { name: 'notify' },
};
/**
 * The composer's slash commands: `own` first (the composer's /goal), then the palette's commands that have a slash
 * spelling, in the palette's order (so a palette command the surface does not offer has no slash command either), and
 * `last` (the composer's /help) at the end.
 */
export function slashCommandsFor(palette, own, last = []) {
    const aliases = palette.flatMap((entry) => {
        const spelling = PALETTE_SLASH_ALIASES[entry.id];
        if (!spelling)
            return [];
        return [{ id: entry.id, name: spelling.name, aliases: spelling.aliases ?? [], description: spelling.description ?? (entry.hint ? `${entry.label} (${entry.hint})` : entry.label), run: () => entry.run() }];
    });
    return [...own, ...aliases, ...last];
}
/**
 * What the composer holds when it may be a slash command: the word after the slash (lower case) and what follows it
 * (trimmed), or null when the text is not one: it does not start with `/`, or the word carries anything but letters,
 * digits and hyphens (`/etc/hosts`, `/goal,`), which is ordinary text the person is typing.
 */
export function slashInput(input) {
    const match = /^\/([a-z0-9-]*)(?:\s([\s\S]*))?$/i.exec(input);
    if (!match)
        return null;
    return { word: match[1].toLowerCase(), args: (match[2] ?? '').trim() };
}
/** The commands matching the typed word, best first (the palette's ranking on `/name`, aliases as keywords); every command for an empty word. */
export function rankSlashCommands(commands, word) {
    const candidates = commands.map((command) => ({ label: `/${command.name}`, keywords: command.aliases.map((alias) => `/${alias}`), command }));
    return rankPalette(candidates, word ? `/${word}` : '').map((candidate) => candidate.command);
}
/** The command `word` names exactly, by name or alias, or undefined (an empty word names nothing). */
export function matchSlashCommand(commands, word) {
    const lower = word.toLowerCase();
    return lower ? commands.find((command) => command.name === lower || command.aliases.includes(lower)) : undefined;
}
/** The /help note: every command with its spellings, usage and description, as a Markdown list. */
export function slashHelpNote(commands) {
    const lines = commands.map((command) => {
        const spellings = [command.name, ...command.aliases].map((name) => `/${name}`).join(', ');
        return `- **${spellings}**${command.usage ? ` ${command.usage}` : ''}: ${command.description}`;
    });
    return `Slash commands (type / at the start of a message):\n${lines.join('\n')}`;
}
export const GOAL_USAGE = 'Use /goal set <text> to save a goal, /goal (or /goal list) to see your goals, and /goal done <number> to remove one.';
/** The usage shown in the menu and /help. */
export const GOAL_USAGE_SHORT = 'set <text> · list · done <n>';
/** The words after /goal: `set <text>` (also add, new), `list` or nothing, `done <n>` (also remove, delete, rm, complete). Anything else asks for the words. */
export function parseGoalArgs(args) {
    const trimmed = args.trim();
    if (!trimmed || /^(?:list|show|ls)$/i.test(trimmed))
        return { op: 'list' };
    const add = /^(?:set|add|new)\s+([\s\S]+)$/i.exec(trimmed);
    if (add)
        return { op: 'add', text: add[1].trim() };
    const remove = /^(?:done|remove|delete|rm|complete)\s+#?(\d{1,3})$/i.exec(trimmed);
    if (remove)
        return { op: 'remove', n: Number(remove[1]) };
    return { op: 'help' };
}
/** The goals as a note: a numbered list, or where to start. */
export function goalsNote(goals) {
    if (!goals.length)
        return `You have no goals saved yet. ${GOAL_USAGE}`;
    return `Your goals:\n${goals.map((goal, i) => `${i + 1}. ${goal}`).join('\n')}`;
}
/**
 * The note for a /goal request's answer (`status` and `body` from GyroClient.memoryGoals). A success names what was saved
 * or removed and lists the goals; a refusal says the service's own words, or a plain line for a status with none.
 */
export function goalResultNote(request, status, body) {
    const answer = (body && typeof body === 'object' ? body : {});
    const goals = Array.isArray(answer.goals) ? answer.goals.filter((goal) => typeof goal === 'string') : [];
    if (status === 200) {
        const limit = typeof answer.limit === 'number' ? answer.limit : 20;
        if (request.op === 'add')
            return `Goal saved: ${typeof answer.added === 'string' ? answer.added : request.text} (${goals.length} of ${limit}).`;
        if (request.op === 'remove')
            return `Goal done: ${typeof answer.removed === 'string' ? answer.removed : `goal ${request.n}`}. ${goals.length ? `${goals.length} ${goals.length === 1 ? 'goal' : 'goals'} left.` : 'No goals left.'}`;
        return goalsNote(goals);
    }
    if (typeof answer.message === 'string' && answer.message.trim())
        return answer.message.trim();
    if (status === 401 || status === 403)
        return 'Goals are not available in this app.';
    if (status === 404 && answer.error === undefined)
        return 'Goals are not available here yet: this Gyro does not have the goals route.';
    if (status === 503)
        return "Gyro's memory could not be reached just now; nothing was changed. Try again in a minute.";
    return `Gyro could not do that (${status}). ${GOAL_USAGE}`;
}
//# sourceMappingURL=slashCommands.js.map